import React, { memo, useCallback, useRef, useState } from "react";
import { Markdown } from "../Markdown";
import { MarkdownToolbar } from "../Markdown/Toolbar/MarkdownToolbar";
import MaxCharacterPlugin from "../Markdown/plugins/common/MaxCharacterPlugin";
import useInput from "../lib/hooks/useInput";
import Button from "../Button";
import t from "../i18n";
import { spacings } from "../Theme";
import { css } from "@linaria/core";
import { CLEAR_EDITOR_COMMAND, type LexicalEditor } from "lexical";
import AdditionnalMessage from "../AdditionnalMessage";
import { MarkdownToolbarTable } from "../Markdown/Toolbar/MarkdownToolbarTable";
import { DEFAULT_CONFIG_TABLE } from "../Markdown/plugins/Table/utils/config";
import { DEFAULT_CLASSNAME_THEME_TABLE } from "../Markdown/plugins/Table/utils/style";

type Props = Omit<
  React.ComponentProps<typeof Markdown>,
  "disabled" | "children" | "onConversion" | "editable"
> &
  Omit<React.HTMLAttributes<HTMLDivElement>, "children"> & {
    maxLength?: number;
    onPublish: (message: string) => void | Promise<void>;
    onCancel: () => void;
    children?: (toolbar: typeof MarkdownToolbar, richText: React.ReactNode) => React.ReactNode;
  };

function MessageEditorV2_({
  children,
  maxLength,
  onPublish,
  onCancel,
  className,
  onFocus,
  onBlur,
  ...markdown
}: Props) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const editor = useRef<LexicalEditor | null>(null);
  const [v, setVersion] = useState<number>(0); // hack, must be better
  const { value, dirty, setValue } = useInput(markdown.defaultValue);
  const [loading, load] = useState<boolean>(false);
  const [showMaxCharCountError, setMaxCharCountError] = useState<boolean>(false);

  const publish = useCallback(
    async (e: React.MouseEvent) => {
      if (!onPublish) return;
      e.stopPropagation();
      load(true);
      await onPublish(value);
      setValue("");
      setVersion(v => v++);
      editor.current?.dispatchCommand(CLEAR_EDITOR_COMMAND, void 0);
      load(false);
    },
    [onPublish, setValue, value],
  );

  const cancel = useCallback(() => {
    setValue(markdown.defaultValue ?? "");
    setVersion(v => v++);
    editor.current?.dispatchCommand(CLEAR_EDITOR_COMMAND, void 0);
    onCancel?.();
  }, [markdown.defaultValue, onCancel, setValue]);

  const focus = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      onFocus?.(e);
    },
    [onFocus],
  );

  const blur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      if (editorRef.current && !editorRef.current.contains(e.relatedTarget as Node | null)) {
        onBlur?.(e);
      }
    },
    [onBlur],
  );
  return (
    <div ref={editorRef} className={className} onFocus={focus} onBlur={blur}>
      <Markdown
        key={v}
        editable
        defaultValue={value}
        disabled={loading}
        onConversion={setValue}
        ref={editor}
        error={showMaxCharCountError}
        config={{ nodes: [...DEFAULT_CONFIG_TABLE.nodes], theme: DEFAULT_CONFIG_TABLE.theme }}
        theme={DEFAULT_CLASSNAME_THEME_TABLE}
        {...markdown}
      >
        {richText => (
          <>
            {typeof children === "function" ? (
              children(MarkdownToolbarTable, richText)
            ) : (
              <>
                <MarkdownToolbarTable />
                {richText}
              </>
            )}
            {maxLength !== undefined && (
              <MaxCharacterPlugin
                maxCharacters={maxLength}
                onCharacterCountChange={charCount => setMaxCharCountError(charCount >= maxLength)}
              />
            )}
          </>
        )}
      </Markdown>
      {value.length > 0 && (
        <footer className={messageEditorActions}>
          {showMaxCharCountError && (
            <AdditionnalMessage
              status="ERROR"
              text={t("project.dashboard.block.comment_rich_text.error.text_too_long")}
              className={messageEditorError}
            />
          )}
          <Button variant="ghost" onClick={cancel} slim>
            {t("message_editor.cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={loading || !dirty || value.length === 0 || showMaxCharCountError}
            onClick={publish}
            slim
          >
            {t("message_editor.publish")}
          </Button>
        </footer>
      )}
    </div>
  );
}
export const MessageEditorV2 = memo(MessageEditorV2_);

//#region CSS
const messageEditorError = css`
  margin-right: auto;
`;

const messageEditorActions = css`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${spacings.xs};
  margin-top: ${spacings.xs};
`;
//#endregion CSS
